import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Zap, RefreshCw, Play, Clock, CheckCircle2, XCircle, AlertTriangle,
  Download, Send, RotateCcw, Bell, BellOff, Loader2, ChevronDown,
  ChevronUp, Activity, ArrowRight, CheckCircle, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import PortalLayout from './PortalLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const JOB_STATUS_MAP = {
  completed: { label: 'Completed', cls: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' },
  partial: { label: 'Partial', cls: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' },
  failed: { label: 'Failed', cls: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' },
  pending: { label: 'Pending', cls: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' },
  processing: { label: 'Running', cls: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' },
};

export default function PortalAutomation() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [config, setConfig] = useState({
    auto_import_enabled: false,
    import_schedule: 'daily',
    import_time: '02:00',
    auto_submit_enabled: false,
    submit_delay_minutes: 15,
    error_notifications: true,
    notification_email: '',
  });
  const [status, setStatus] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [errors, setErrors] = useState([]);
  const [jobFilter, setJobFilter] = useState('all');
  const [showErrors, setShowErrors] = useState(false);
  const token = localStorage.getItem('einvoice_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/einvoice/portal/auth'); return; }
    loadAll();
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, statusRes, jobsRes, errorsRes] = await Promise.all([
        axios.get(`${API_URL}/api/einvoice/automation/config`, { headers }),
        axios.get(`${API_URL}/api/einvoice/automation/status`, { headers }),
        axios.get(`${API_URL}/api/einvoice/automation/jobs?limit=30`, { headers }),
        axios.get(`${API_URL}/api/einvoice/automation/errors?limit=10`, { headers }),
      ]);
      setConfig(configRes.data);
      setStatus(statusRes.data);
      setJobs(jobsRes.data.jobs || []);
      setErrors(errorsRes.data.errors || []);
    } catch (err) {
      console.error('Failed to load automation data:', err);
    } finally { setLoading(false); }
  }, [headers]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/einvoice/automation/config`, config, { headers });
      toast.success('Automation settings saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save settings'); }
    finally { setSaving(false); }
  };

  const triggerSync = async () => {
    setSyncing(true);
    try {
      const propId = localStorage.getItem('einvoice_selected_property');
      const { data } = await axios.post(`${API_URL}/api/einvoice/automation/trigger-sync`, { property_id: propId || null }, { headers });
      toast.success(data.message);
      await loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const triggerSubmit = async () => {
    setSubmitting(true);
    try {
      const propId = localStorage.getItem('einvoice_selected_property');
      const { data } = await axios.post(`${API_URL}/api/einvoice/automation/trigger-submit`, { property_id: propId || null }, { headers });
      toast.success(data.message);
      await loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Submit failed'); }
    finally { setSubmitting(false); }
  };

  const retryJob = async (jobId) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/einvoice/automation/retry/${jobId}`, {}, { headers });
      toast.success(data.message || 'Job retried');
      await loadAll();
    } catch (err) { toast.error('Retry failed'); }
  };

  const filteredJobs = jobFilter === 'all' ? jobs : jobs.filter(j => j.job_type === jobFilter);

  const formatTime = (iso) => {
    if (!iso) return 'Never';
    try {
      const d = new Date(iso);
      const now = new Date();
      const diff = now - d;
      if (diff < 60000) return 'Just now';
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
      return d.toLocaleDateString('en-MY', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  // Deduplicate errors by message for cleaner display
  const uniqueErrors = [];
  const seenMsgs = new Set();
  for (const err of errors) {
    const key = err.error;
    if (!seenMsgs.has(key)) {
      seenMsgs.add(key);
      uniqueErrors.push({ ...err, count: errors.filter(e => e.error === key).length });
    }
  }

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        </div>
      </PortalLayout>
    );
  }

  const syncRuns = status?.pms_sync?.total_runs || 0;
  const syncSuccess = status?.pms_sync?.success_count || 0;
  const syncRate = syncRuns > 0 ? Math.round((syncSuccess / syncRuns) * 100) : 0;
  const submitRuns = status?.lhdn_submit?.total_runs || 0;
  const submitSuccess = status?.lhdn_submit?.success_count || 0;
  const submitRate = submitRuns > 0 ? Math.round((submitSuccess / submitRuns) * 100) : 0;

  return (
    <PortalLayout>
      <div className="p-4 sm:p-6 max-w-[1400px] mx-auto space-y-6" data-testid="portal-automation">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="automation-title">Automation</h1>
            <p className="text-slate-500 dark:text-gray-400 text-sm mt-0.5">Schedule PMS sync and auto-submit invoices to LHDN</p>
          </div>
          <Button size="sm" onClick={loadAll} className="h-8 text-xs bg-slate-600 hover:bg-slate-700 text-white" data-testid="refresh-automation-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>

        {/* ── PMS Connection Status Banner ── */}
        {!status?.pms_connected && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 flex items-start gap-3" data-testid="pms-not-connected-banner">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-amber-800 dark:text-amber-300 text-sm">No PMS Connected</p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                Beds24 tokens are not configured for this e-Invoice account. PMS Booking Sync will not work until you connect your Beds24 account.
              </p>
              <a href="/einvoice/portal/integrations" className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline" data-testid="go-to-integrations-link">
                Go to Integrations to connect Beds24 <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        )}

        {status?.pms_connected && (
          <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-3 flex items-center gap-3" data-testid="pms-connected-banner">
            <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
            <p className="text-xs text-emerald-700 dark:text-emerald-400">
              <span className="font-medium">Beds24 Connected</span> — PMS Booking Sync is ready
            </p>
          </div>
        )}

        {/* ── Quick Action Cards ── */}
        <div className="grid md:grid-cols-2 gap-4" data-testid="automation-status-cards">
          {/* PMS Sync Card */}
          <Card className="border-slate-200/80 dark:border-zinc-800 overflow-hidden">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                    <Download className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white text-sm">PMS Booking Sync</h3>
                    {status?.auto_import_enabled ? (
                      <Badge className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-0 text-[10px] px-1.5 py-0 mt-0.5">
                        Active &middot; {status.import_schedule}
                      </Badge>
                    ) : (
                      <span className="text-[11px] text-slate-400 dark:text-gray-500">Not scheduled</span>
                    )}
                  </div>
                </div>
                <Button size="sm" onClick={triggerSync} disabled={syncing}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8" data-testid="trigger-sync-btn">
                  {syncing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1" />}
                  Sync Now
                </Button>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-slate-50 dark:bg-zinc-900 rounded-lg p-2.5 text-center">
                  <p className="text-xl font-bold text-slate-900 dark:text-white">{syncRuns}</p>
                  <p className="text-[10px] text-slate-400 dark:text-gray-500">Runs</p>
                </div>
                <div className="flex-1 bg-slate-50 dark:bg-zinc-900 rounded-lg p-2.5 text-center">
                  <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{syncRate}%</p>
                  <p className="text-[10px] text-slate-400 dark:text-gray-500">Success</p>
                </div>
                <div className="flex-1">
                  <p className="text-[10px] text-slate-400 dark:text-gray-500">Last run</p>
                  <p className="text-xs font-medium text-slate-600 dark:text-gray-300">{status?.pms_sync?.last_sync ? formatTime(status.pms_sync.last_sync.created_at) : 'Never'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* LHDN Submit Card */}
          <Card className="border-slate-200/80 dark:border-zinc-800 overflow-hidden">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
                    <Send className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white text-sm">LHDN Auto-Submit</h3>
                    {status?.auto_submit_enabled ? (
                      <Badge className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-0 text-[10px] px-1.5 py-0 mt-0.5">Active</Badge>
                    ) : (
                      <span className="text-[11px] text-slate-400 dark:text-gray-500">Not scheduled</span>
                    )}
                  </div>
                </div>
                <Button size="sm" onClick={triggerSubmit} disabled={submitting}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8" data-testid="trigger-submit-btn">
                  {submitting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                  Submit Now
                </Button>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-slate-50 dark:bg-zinc-900 rounded-lg p-2.5 text-center">
                  <p className="text-xl font-bold text-slate-900 dark:text-white">{submitRuns}</p>
                  <p className="text-[10px] text-slate-400 dark:text-gray-500">Runs</p>
                </div>
                <div className="flex-1 bg-slate-50 dark:bg-zinc-900 rounded-lg p-2.5 text-center">
                  <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{submitRate}%</p>
                  <p className="text-[10px] text-slate-400 dark:text-gray-500">Success</p>
                </div>
                <div className="flex-1">
                  <p className="text-[10px] text-slate-400 dark:text-gray-500">Last run</p>
                  <p className="text-xs font-medium text-slate-600 dark:text-gray-300">{status?.lhdn_submit?.last_submit ? formatTime(status.lhdn_submit.last_submit.created_at) : 'Never'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>


        {/* ── Automation Settings ── */}
        <Card className="border-slate-200/80 dark:border-zinc-800" data-testid="automation-config">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2 text-slate-800 dark:text-white">
              <Zap className="w-4 h-4 text-blue-500" /> Automation Settings
            </CardTitle>
            <CardDescription className="text-xs">Configure scheduled imports and auto-submission</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Auto Import */}
            <div className="p-4 rounded-xl border border-slate-200 dark:border-zinc-800 bg-slate-50/50 dark:bg-zinc-900/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                    <Download className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900 dark:text-white text-sm">Auto-Import Bookings</h4>
                    <p className="text-[11px] text-slate-400 dark:text-gray-500">Automatically sync bookings from your PMS</p>
                  </div>
                </div>
                <Switch checked={config.auto_import_enabled}
                  onCheckedChange={v => setConfig({ ...config, auto_import_enabled: v })}
                  data-testid="toggle-auto-import" />
              </div>
              {config.auto_import_enabled && (
                <div className="grid grid-cols-2 gap-4 mt-4 pt-3 border-t border-slate-200 dark:border-zinc-700">
                  <div>
                    <Label className="text-[11px] text-slate-500 dark:text-gray-400 mb-1">Schedule</Label>
                    <Select value={config.import_schedule} onValueChange={v => setConfig({ ...config, import_schedule: v })}>
                      <SelectTrigger className="h-9" data-testid="import-schedule-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hourly">Every Hour</SelectItem>
                        <SelectItem value="every_6h">Every 6 Hours</SelectItem>
                        <SelectItem value="daily">Daily</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {config.import_schedule === 'daily' && (
                    <div>
                      <Label className="text-[11px] text-slate-500 dark:text-gray-400 mb-1">Time (MYT - Malaysia)</Label>
                      <Input type="time" value={config.import_time} onChange={e => setConfig({ ...config, import_time: e.target.value })}
                        className="h-9" data-testid="import-time-input" />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Auto Submit */}
            <div className="p-4 rounded-xl border border-slate-200 dark:border-zinc-800 bg-slate-50/50 dark:bg-zinc-900/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
                    <Send className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900 dark:text-white text-sm">Auto-Submit to LHDN</h4>
                    <p className="text-[11px] text-slate-400 dark:text-gray-500">Automatically submit invoices after import</p>
                  </div>
                </div>
                <Switch checked={config.auto_submit_enabled}
                  onCheckedChange={v => setConfig({ ...config, auto_submit_enabled: v })}
                  data-testid="toggle-auto-submit" />
              </div>
              {config.auto_submit_enabled && (
                <div className="mt-4 pt-3 border-t border-slate-200 dark:border-zinc-700">
                  <Label className="text-[11px] text-slate-500 dark:text-gray-400 mb-1">Delay after import (minutes)</Label>
                  <Input type="number" min={0} max={120} value={config.submit_delay_minutes}
                    onChange={e => setConfig({ ...config, submit_delay_minutes: parseInt(e.target.value) || 0 })}
                    className="w-32 h-9" data-testid="submit-delay-input" />
                  <p className="text-[10px] text-slate-400 dark:text-gray-500 mt-1">Wait before submitting to LHDN</p>
                </div>
              )}
            </div>

            {/* Notifications */}
            <div className="p-4 rounded-xl border border-slate-200 dark:border-zinc-800 bg-slate-50/50 dark:bg-zinc-900/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
                    {config.error_notifications ? <Bell className="w-4 h-4 text-amber-600 dark:text-amber-400" /> : <BellOff className="w-4 h-4 text-slate-400 dark:text-gray-500" />}
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900 dark:text-white text-sm">Error Notifications</h4>
                    <p className="text-[11px] text-slate-400 dark:text-gray-500">Get notified when sync or submit fails</p>
                  </div>
                </div>
                <Switch checked={config.error_notifications}
                  onCheckedChange={v => setConfig({ ...config, error_notifications: v })}
                  data-testid="toggle-notifications" />
              </div>
              {config.error_notifications && (
                <div className="mt-4 pt-3 border-t border-slate-200 dark:border-zinc-700">
                  <Label className="text-[11px] text-slate-500 dark:text-gray-400 mb-1">Notification Email</Label>
                  <Input type="email" value={config.notification_email}
                    onChange={e => setConfig({ ...config, notification_email: e.target.value })}
                    placeholder="admin@hotel.com" className="h-9" data-testid="notification-email-input" />
                </div>
              )}
            </div>

            <Button onClick={saveConfig} disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm h-9" data-testid="save-automation-btn">
              {saving ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1.5" />}
              Save Settings
            </Button>
          </CardContent>
        </Card>

        {/* ── Job History ── */}
        <Card className="border-slate-200/80 dark:border-zinc-800" data-testid="job-history">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2 text-slate-800 dark:text-white">
              <Activity className="w-4 h-4 text-blue-500" /> Job History
            </CardTitle>
            <div className="flex gap-1.5">
              {['all', 'pms_sync', 'lhdn_submit'].map(f => (
                <Button key={f} variant={jobFilter === f ? 'default' : 'ghost'} size="sm"
                  onClick={() => setJobFilter(f)} data-testid={`job-filter-${f}`}
                  className={`text-xs h-7 ${jobFilter === f ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-800 dark:text-gray-400'}`}>
                  {f === 'all' ? 'All' : f === 'pms_sync' ? 'PMS Sync' : 'LHDN Submit'}
                </Button>
              ))}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {filteredJobs.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="jobs-table">
                  <thead>
                    <tr className="border-y border-slate-200 dark:border-zinc-800 bg-slate-50/80 dark:bg-zinc-900/50">
                      <th className="text-left px-4 py-2.5 text-[11px] font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide">Job</th>
                      <th className="text-left px-4 py-2.5 text-[11px] font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide">Type</th>
                      <th className="text-center px-4 py-2.5 text-[11px] font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide">Status</th>
                      <th className="text-right px-4 py-2.5 text-[11px] font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide">Processed</th>
                      <th className="text-left px-4 py-2.5 text-[11px] font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide">Trigger</th>
                      <th className="text-left px-4 py-2.5 text-[11px] font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide">Time</th>
                      <th className="text-right px-4 py-2.5 text-[11px] font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredJobs.map(j => {
                      const st = JOB_STATUS_MAP[j.status] || JOB_STATUS_MAP.pending;
                      return (
                        <tr key={j.job_id} className="border-b border-slate-100 dark:border-zinc-800/50 hover:bg-slate-50/50 dark:hover:bg-zinc-900/30 transition-colors">
                          <td className="px-4 py-2.5 font-mono text-xs text-blue-600 dark:text-blue-400">{j.job_id?.substring(0, 8)}</td>
                          <td className="px-4 py-2.5">
                            <span className="text-xs text-slate-600 dark:text-gray-300">
                              {j.job_type === 'pms_sync' ? 'PMS Sync' : 'LHDN Submit'}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.label}</span>
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <span className="font-medium text-slate-800 dark:text-white">{j.items_processed || 0}</span>
                            {(j.items_failed || 0) > 0 && (
                              <span className="text-[10px] text-amber-500 ml-1">({j.items_failed} issues)</span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-xs text-slate-400 dark:text-gray-500 capitalize">{j.triggered_by || 'manual'}</td>
                          <td className="px-4 py-2.5 text-xs text-slate-400 dark:text-gray-500">{formatTime(j.created_at)}</td>
                          <td className="px-4 py-2.5 text-right">
                            {(j.status === 'failed' || j.status === 'partial') && (
                              <Button variant="ghost" size="sm" onClick={() => retryJob(j.job_id)}
                                className="text-xs h-7 px-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20">
                                <RotateCcw className="w-3 h-3 mr-1" /> Retry
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Activity className="w-10 h-10 mx-auto text-slate-200 dark:text-gray-700 mb-2" />
                <p className="text-sm font-medium text-slate-600 dark:text-gray-300">No automation jobs yet</p>
                <p className="text-xs text-slate-400 dark:text-gray-500 mt-1">Click "Sync Now" or "Submit Now" to run your first job</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PortalLayout>
  );
}
